import { extendDisplayView } from "../../i18n/display-view.js";

/**
 * Area `entitlement` of the console gateway — over `apps/api/src/routes/entitlement/` (design §27.9.2). Owned by plan-014
 * unit **E3a (then E3b)**; filled by E3b (FR12-ENT-02/06/09).
 *
 *   readEntitlements(accountId)                         `GET /v1/accounts/{id}/entitlements` — the organization's
 *                                                       allowances now: talk-second (and other) grants with their
 *                                                       workspace allocations and unallocated remainder, each balance's
 *                                                       trial and what open conversations hold, each live workspace's
 *                                                       spend cap with this month's charged talk, and the concurrency
 *                                                       entitlement admission enforces (with where it comes from)
 *   changeEntitlementAllocations(accountId, change, r)  `PUT /v1/accounts/{id}/entitlement-allocations` — a shared
 *                                                       talk-second grant's allocations (If-Match: its allocation_revision)
 *   changeSpendCap(workspaceId, change, revision)       `PUT /v1/workspaces/{id}/spend-cap` — the workspace's monthly cap,
 *                                                       its behaviour at the cap and the warning (If-Match: the cap's
 *                                                       revision; `0` creates it)
 *
 * WHO MAY (the API decides; the console only OFFERS): reading is `account.finance.read`, allocating is
 * `account.finance.manage`, a spend cap is `spend_cap.write` held through a role IN that workspace (Owner, Admin).
 * The warning is a notification and never refuses (FR12-ENT-06) — the view keeps it apart from the cap.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

/** What happens when a workspace reaches its cap (0098's CHECK). */
export type SpendCapBehaviorView = "hard_stop" | "capped_overage" | "notify_only";
export const SPEND_CAP_BEHAVIOR: readonly SpendCapBehaviorView[] = ["hard_stop", "capped_overage", "notify_only"];

/** The units an entitlement grant is counted in (0098). */
export type EntitlementKindView = "talk_second" | "storage_byte" | "seat" | "resource" | "concurrency_unit";
const ENTITLEMENT_KIND: readonly EntitlementKindView[] = ["talk_second", "storage_byte", "seat", "resource", "concurrency_unit"];

/** Where an organization's concurrency entitlement comes from. */
export type ConcurrencySourceView = "tier" | "revision" | "quota_request" | "add_on" | "time_bound_raise";
const CONCURRENCY_SOURCE: readonly ConcurrencySourceView[] = ["tier", "revision", "quota_request", "add_on", "time_bound_raise"];

export interface EntitlementAllocationView {
  readonly entitlementAllocationId: string;
  readonly workspaceId: string;
  readonly allocatedUnit: number;
  /** Held by the workspace's open conversations. */
  readonly reservedUnit: number;
  /** Settled by day close. */
  readonly consumedUnit: number;
}

export interface EntitlementGrantView {
  readonly entitlementGrantId: string;
  readonly billingAccountId: string;
  readonly kind: EntitlementKindView;
  readonly grantedUnit: number;
  readonly reservedUnit: number;
  readonly consumedUnit: number;
  /** Granted less Σ allocated. */
  readonly unallocatedUnit: number;
  readonly activeFrom: string;
  readonly activeUntil: string | null;
  readonly source: string;
  /** The If-Match of an allocation change. */
  readonly allocationRevision: number;
  readonly allocation: readonly EntitlementAllocationView[];
}

export interface EntitlementBalanceView {
  readonly billingAccountId: string;
  readonly currency: string;
  readonly trialGrantedSecond: number;
  readonly trialRemainingSecond: number;
  readonly heldSecond: number;
  readonly heldMinor: number;
  readonly heldConversationCount: number;
}

/** A workspace's spend cap as stored — the cap, the behaviour at it, and the warning apart. */
export interface SpendCapView {
  readonly monthBudgetMinor: number;
  readonly capBehavior: SpendCapBehaviorView;
  /** Exactly for `capped_overage`: how far past the cap new conversations may still start. */
  readonly overageMinor: number | null;
  /** A notification threshold, never enforcement. */
  readonly warnAtPercent: number;
  readonly currency: string;
  readonly revision: number;
}

export interface EntitlementWorkspaceView {
  readonly workspaceId: string;
  readonly workspaceName: string;
  /** This month's charged talk (the workspace's own month). */
  readonly monthChargedMinor: number;
  /** Paid talk this month as time and money (INT 2026-10-07): the charged seconds and what they cost. */
  readonly monthPaidTalk: { readonly second: number; readonly minor: number };
  readonly spendCap: SpendCapView | null;
}

export interface EntitlementOverviewView {
  readonly at: string;
  readonly grant: readonly EntitlementGrantView[];
  readonly balance: readonly EntitlementBalanceView[];
  readonly workspace: readonly EntitlementWorkspaceView[];
  /** What admission enforces: max(the tier's channel units, the largest granted request that has not expired). */
  readonly concurrency: { readonly entitlement: number; readonly source: readonly ConcurrencySourceView[] };
}

/** A spend cap change. `overageMinor` is required for `capped_overage` and refused for anything else. */
export interface SpendCapChangeView {
  readonly monthBudgetMinor: number;
  readonly capBehavior: SpendCapBehaviorView;
  readonly overageMinor: number | null;
  readonly warnAtPercent: number;
}

/** A spend cap after a change, and whether the caller may change it again. */
export interface SpendCapChangedView extends SpendCapView {
  readonly workspaceId: string;
  readonly canChange: boolean;
}

/** A shared talk-second grant's new allocations. */
export interface EntitlementAllocationChangeView {
  readonly entitlementGrantId: string;
  readonly allocation: readonly { readonly workspaceId: string; readonly allocatedUnit: number }[];
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-014 E3b: the organization's allowances, spend caps and concurrency entitlement now. */
    readEntitlements(accountId: string): Promise<EntitlementOverviewView>;
    /** plan-014 E3b: a shared grant's workspace allocations; `revision` is the grant's allocation revision read. */
    changeEntitlementAllocations(accountId: string, change: EntitlementAllocationChangeView, revision: number): Promise<EntitlementGrantView>;
    /** plan-014 E3b: a workspace's spend cap; `revision` is the cap's revision read, `0` when it has none yet. */
    changeSpendCap(workspaceId: string, change: SpendCapChangeView, revision: number): Promise<SpendCapChangedView>;
  }
}

export type EntitlementGatewaySlice = Pick<ConsoleGateway, "readEntitlements" | "changeEntitlementAllocations" | "changeSpendCap">;

/** This area's rows of `GATEWAY_GAP`: none — E3a's three operations are in the assembled spec. */
export const entitlementGap: readonly GatewayGap[] = [];

// ── decoding (exported for the suite and the sandbox) ─────────────────────────────────────────────────────

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const found = allowed.find((one) => one === value);
  if (found === undefined) throw new Error(`${path}: ${JSON.stringify(value)} is not one of ${allowed.join(", ")}`);
  return found;
}

function wholeOrNull(value: unknown, path: string): number | null {
  return value === null || value === undefined ? null : readWholeNumber(value, path);
}

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function paidTalkOfWire(data: Readonly<Record<string, unknown>>, path: string): EntitlementWorkspaceView["monthPaidTalk"] {
  return { second: readWholeNumber(data["second"], `${path}.second`), minor: readWholeNumber(data["minor"], `${path}.minor`) };
}

export function spendCapOfWire(data: Readonly<Record<string, unknown>>, path: string): SpendCapView {
  return {
    monthBudgetMinor: readWholeNumber(data["month_budget_minor"], `${path}.month_budget_minor`),
    capBehavior: oneOf(SPEND_CAP_BEHAVIOR, data["cap_behavior"], `${path}.cap_behavior`),
    overageMinor: wholeOrNull(data["overage_minor"], `${path}.overage_minor`),
    warnAtPercent: readWholeNumber(data["warn_at_percent"], `${path}.warn_at_percent`),
    currency: readText(data["currency"], `${path}.currency`),
    revision: readWholeNumber(data["revision"], `${path}.revision`),
  };
}

export function entitlementGrantOfWire(data: Readonly<Record<string, unknown>>, path: string): EntitlementGrantView {
  const unallocated = data["unallocated_unit"];
  if (typeof unallocated !== "number" || !Number.isSafeInteger(unallocated)) throw new Error(`${path}.unallocated_unit must be a whole number`);
  return {
    entitlementGrantId: readText(data["entitlement_grant_id"], `${path}.entitlement_grant_id`),
    billingAccountId: readText(data["billing_account_id"], `${path}.billing_account_id`),
    kind: oneOf(ENTITLEMENT_KIND, data["kind"], `${path}.kind`),
    grantedUnit: readWholeNumber(data["granted_unit"], `${path}.granted_unit`),
    reservedUnit: readWholeNumber(data["reserved_unit"], `${path}.reserved_unit`),
    consumedUnit: readWholeNumber(data["consumed_unit"], `${path}.consumed_unit`),
    unallocatedUnit: unallocated,
    activeFrom: readText(data["active_from"], `${path}.active_from`),
    activeUntil: textOrNull(data["active_until"], `${path}.active_until`),
    source: readText(data["source"], `${path}.source`),
    allocationRevision: readWholeNumber(data["allocation_revision"], `${path}.allocation_revision`),
    allocation: readArray(data["allocation"], `${path}.allocation`).map((raw, index) => {
      const one = readRecord(raw, `${path}.allocation[${index}]`);
      return {
        entitlementAllocationId: readText(one["entitlement_allocation_id"], `${path}.allocation.entitlement_allocation_id`),
        workspaceId: readText(one["workspace_id"], `${path}.allocation.workspace_id`),
        allocatedUnit: readWholeNumber(one["allocated_unit"], `${path}.allocation.allocated_unit`),
        reservedUnit: readWholeNumber(one["reserved_unit"], `${path}.allocation.reserved_unit`),
        consumedUnit: readWholeNumber(one["consumed_unit"], `${path}.allocation.consumed_unit`),
      };
    }),
  };
}

export function entitlementOverviewOfWire(data: Readonly<Record<string, unknown>>, path: string): EntitlementOverviewView {
  const concurrency = readRecord(data["concurrency"], `${path}.concurrency`);
  return {
    at: readText(data["at"], `${path}.at`),
    grant: readArray(data["grant"], `${path}.grant`).map((raw, index) => entitlementGrantOfWire(readRecord(raw, `${path}.grant[${index}]`), `${path}.grant[${index}]`)),
    balance: readArray(data["billing_account"], `${path}.billing_account`).map((raw, index) => {
      const at = `${path}.billing_account[${index}]`;
      const one = readRecord(raw, at);
      const trial = readRecord(one["trial"], `${at}.trial`);
      const held = readRecord(one["held"], `${at}.held`);
      return {
        billingAccountId: readText(one["billing_account_id"], `${at}.billing_account_id`),
        currency: readText(one["currency"], `${at}.currency`),
        trialGrantedSecond: readWholeNumber(trial["granted_second"], `${at}.trial.granted_second`),
        trialRemainingSecond: readWholeNumber(trial["remaining_second"], `${at}.trial.remaining_second`),
        heldSecond: readWholeNumber(held["reserved_second"], `${at}.held.reserved_second`),
        heldMinor: readWholeNumber(held["reserved_minor"], `${at}.held.reserved_minor`),
        heldConversationCount: readWholeNumber(held["conversation_count"], `${at}.held.conversation_count`),
      };
    }),
    workspace: readArray(data["workspace"], `${path}.workspace`).map((raw, index) => {
      const at = `${path}.workspace[${index}]`;
      const one = readRecord(raw, at);
      const cap = one["spend_cap"];
      return {
        workspaceId: readText(one["workspace_id"], `${at}.workspace_id`),
        workspaceName: readText(one["workspace_name"], `${at}.workspace_name`),
        monthChargedMinor: readWholeNumber(one["month_charged_minor"], `${at}.month_charged_minor`),
        monthPaidTalk: paidTalkOfWire(readRecord(one["month_paid_talk"], `${at}.month_paid_talk`), `${at}.month_paid_talk`),
        spendCap: cap === null || cap === undefined ? null : spendCapOfWire(readRecord(cap, `${at}.spend_cap`), `${at}.spend_cap`),
      };
    }),
    concurrency: {
      entitlement: readWholeNumber(concurrency["entitlement"], `${path}.concurrency.entitlement`),
      source: readArray(concurrency["source"], `${path}.concurrency.source`).map((one, index) =>
        oneOf(CONCURRENCY_SOURCE, one, `${path}.concurrency.source[${index}]`),
      ),
    },
  };
}

/** The change on the wire (snake_case; `overage_minor` only for a capped overage, as the API requires). */
export function spendCapChangeBodyOf(change: SpendCapChangeView): Readonly<Record<string, unknown>> {
  return {
    month_budget_minor: change.monthBudgetMinor,
    cap_behavior: change.capBehavior,
    overage_minor: change.capBehavior === "capped_overage" ? change.overageMinor : null,
    warn_at_percent: change.warnAtPercent,
  };
}

/** `If-Match: "<revision>"`. */
export function ifMatchOf(revision: number): Readonly<Record<string, string>> {
  return { "if-match": `"${String(revision)}"` };
}

function dataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(assertOk(result), operationId)["data"], `${operationId}.data`);
}

export function entitlementApiSlice(client: WeTalkApiClient, _context: SliceContext): EntitlementGatewaySlice {
  return {
    async readEntitlements(accountId) {
      const data = dataOf(await client.getEntitlements({ path: { account_id: accountId } }), "getEntitlements");
      return entitlementOverviewOfWire(data, "getEntitlements.data");
    },
    async changeEntitlementAllocations(accountId, change, revision) {
      const data = dataOf(
        await client.putEntitlementAllocations({
          path: { account_id: accountId },
          body: {
            entitlement_grant_id: change.entitlementGrantId,
            allocation: change.allocation.map((one) => ({ workspace_id: one.workspaceId, allocated_unit: one.allocatedUnit })),
          },
          header: ifMatchOf(revision),
        }),
        "putEntitlementAllocations",
      );
      return entitlementGrantOfWire(data, "putEntitlementAllocations.data");
    },
    async changeSpendCap(workspaceId, change, revision) {
      const data = dataOf(
        await client.putSpendCap({ path: { workspace_id: workspaceId }, body: spendCapChangeBodyOf(change), header: ifMatchOf(revision) }),
        "putSpendCap",
      );
      const canChange = data["can_change"];
      if (typeof canChange !== "boolean") throw new Error("putSpendCap.data.can_change must be a boolean");
      return extendDisplayView(spendCapOfWire(data, "putSpendCap.data"), { workspaceId: readText(data["workspace_id"], "putSpendCap.data.workspace_id"), canChange });
    },
  };
}
